import BlogLayout from '@/pages/blog/components/BlogLayout';
import Breadcrumbs from '@/pages/blog/components/Breadcrumbs';
import TableOfContents from '@/pages/blog/components/TableOfContents';
import AuthorBio from '@/pages/blog/components/AuthorBio';
import RelatedArticles from '@/pages/blog/components/RelatedArticles';

const tocItems = [
  { id: 'intro', label: 'Arthritis and Exercise: The Counterintuitive Truth' },
  { id: 'types', label: 'Types of Arthritis That Affect Men Over 60' },
  { id: 'why-exercise', label: 'Why Exercise Is the Best Medicine for Arthritis' },
  { id: 'best-exercises', label: 'Best Exercises for Men with Arthritis Over 60' },
  { id: 'exercises-avoid', label: 'Exercises to Modify or Avoid' },
  { id: 'joint-protection', label: 'Joint Protection Principles' },
  { id: 'flare-ups', label: 'Exercising During Flare-Ups' },
  { id: 'mason-approach', label: "Mason's Joint-Safe Approach" },
  { id: 'faq', label: 'Frequently Asked Questions' },
];

const relatedArticles = [
  { slug: 'joint-pain-exercises-for-seniors', title: 'Exercise WITH Joint Pain: Safe Movements for Men Over 60', category: 'Health & Recovery' },
  { slug: 'knee-friendly-exercises-for-seniors', title: '10 Knee-Friendly Exercises for Men Over 60', category: 'Health & Recovery' },
  { slug: 'weight-training-over-60-safety-tips', title: 'Weight Training Over 60: 10 Safety Tips Every Man Needs to Know', category: 'Strength Training' },
];

const schema = JSON.stringify([
  {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Working Out with Arthritis Over 60: What You Can Still Do',
    author: { '@type': 'Person', name: 'Mason Hale', url: 'https://masonhalefit.com' },
    publisher: { '@type': 'Organization', name: 'Mason Hale Fitness', url: 'https://masonhalefit.com' },
    datePublished: '2024-02-25',
    dateModified: '2024-04-06',
    description: 'Arthritis doesn\'t have to stop you from training. Learn the best exercises for men over 60 with arthritis, what to avoid, and how to train pain-free.',
    mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://masonhalefit.com/blog/exercise-with-arthritis-over-60' },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Can men with arthritis lift weights?',
        acceptedAnswer: { '@type': 'Answer', text: 'Yes. Resistance training is actually recommended for arthritis management. Stronger muscles around arthritic joints provide better support and reduce pain. The key is using appropriate weights, avoiding painful ranges of motion, and progressing gradually.' },
      },
      {
        '@type': 'Question',
        name: 'Does exercise make arthritis worse?',
        acceptedAnswer: { '@type': 'Answer', text: 'No — the opposite is true. Regular, appropriate exercise reduces arthritis pain, improves joint function, and slows disease progression. Inactivity is actually harmful for arthritic joints, causing stiffness, muscle weakness, and increased pain.' },
      },
      {
        '@type': 'Question',
        name: 'What is the best exercise for arthritis in the knees?',
        acceptedAnswer: { '@type': 'Answer', text: 'Low-impact exercises that strengthen the muscles around the knee without excessive joint stress: cycling, swimming, leg press (limited range), straight leg raises, and seated leg extensions. Avoid deep squats and high-impact activities during flare-ups.' },
      },
      {
        '@type': 'Question',
        name: 'Should I exercise when my arthritis is flaring up?',
        acceptedAnswer: { '@type': 'Answer', text: 'During a significant flare-up, reduce intensity and focus on gentle range-of-motion exercises and light walking. Complete rest is rarely beneficial. Once the flare subsides, gradually return to your normal program.' },
      },
    ],
  },
]);

export default function ExerciseWithArthritisPage() {
  return (
    <BlogLayout
      title="Working Out with Arthritis Over 60: What You Can Still Do"
      description="Arthritis doesn't have to stop you from training. Best exercises for men over 60 with arthritis, what to avoid, and how to train pain-free."
      keywords="exercise with arthritis over 60, working out with arthritis, arthritis exercises men over 60, joint-safe exercise arthritis"
      canonicalPath="/blog/exercise-with-arthritis-over-60"
      datePublished="2024-02-25"
      dateModified="2024-04-06"
      schemaMarkup={schema}
    >
      <Breadcrumbs title="Working Out with Arthritis Over 60: What You Can Still Do" />

      <div className="mb-6">
        <span className="text-[#c8a96e] text-xs font-bold tracking-[0.2em] uppercase">Health Conditions</span>
      </div>

      <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4 mb-8">
        <p className="text-yellow-300 text-sm font-bold mb-1">Medical Disclaimer</p>
        <p className="text-yellow-200/70 text-xs leading-relaxed">This article is for informational purposes only. Consult your physician or rheumatologist before starting an exercise program if you have arthritis, especially if your condition is severe or recently diagnosed.</p>
      </div>

      <h1 className="text-white text-3xl sm:text-4xl font-black leading-tight mb-4">
        Working Out with Arthritis Over 60: What You Can Still Do
      </h1>

      <p className="text-gray-400 text-sm mb-8">
        By <strong className="text-white">Mason Hale</strong> &nbsp;·&nbsp; February 25, 2024 &nbsp;·&nbsp; 10 min read
      </p>

      <TableOfContents items={tocItems} />

      <section id="intro" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Arthritis and Exercise: The Counterintuitive Truth</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          Most men with arthritis think exercise will make things worse. Move less, protect the joints, avoid pain. It sounds logical. It's wrong.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">
          The research is clear: appropriate exercise is one of the most effective treatments for arthritis. It reduces pain, improves joint function, builds the muscle that supports damaged joints, and slows disease progression. Inactivity, on the other hand, causes muscle weakness, joint stiffness, and increased pain.
        </p>
        <p className="text-gray-300 leading-relaxed">
          The key word is "appropriate." Not all exercise is equal when you have arthritis. This guide tells you exactly what works, what to modify, and how to train smart with arthritic joints.
        </p>
      </section>

      <section id="types" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Types of Arthritis That Affect Men Over 60</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          {[
            { type: 'Osteoarthritis (OA)', desc: 'The most common type. Cartilage breakdown in joints — most often knees, hips, and hands. Exercise is strongly recommended and highly effective.' },
            { type: 'Rheumatoid Arthritis (RA)', desc: 'An autoimmune condition causing joint inflammation. Exercise is beneficial but requires more careful management around flare-ups.' },
            { type: 'Gout', desc: 'Uric acid crystal deposits in joints. Exercise helps with weight management and metabolic health, which reduces gout frequency.' },
            { type: 'Psoriatic Arthritis', desc: 'Associated with psoriasis. Exercise reduces inflammation and improves joint function, similar to RA management.' },
          ].map((item) => (
            <div key={item.type} className="bg-[#111111] border border-white/10 rounded-lg p-4">
              <p className="text-white text-sm font-bold mb-1">{item.type}</p>
              <p className="text-gray-400 text-xs leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="why-exercise" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Why Exercise Is the Best Medicine for Arthritis</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          The <a href="https://www.cdc.gov/arthritis/basics/physical-activity-overview.htm" target="_blank" rel="noopener noreferrer" className="text-[#c8a96e] hover:text-[#d9bc82] underline">CDC recommends</a> physical activity as a first-line treatment for arthritis. Here's why:
        </p>
        <ul className="space-y-3 mb-4">
          {[
            { title: 'Reduces pain', desc: 'Exercise triggers the release of endorphins and reduces inflammatory markers. Most men with arthritis report less pain after establishing a regular exercise routine.' },
            { title: 'Strengthens supporting muscles', desc: 'Strong muscles around arthritic joints absorb shock and reduce the load on damaged cartilage. This is especially important for knee and hip OA.' },
            { title: 'Maintains joint range of motion', desc: 'Regular movement prevents the stiffness and loss of mobility that comes with inactivity.' },
            { title: 'Reduces body weight', desc: 'Every pound of body weight puts 4 pounds of pressure on the knees. Losing 10 lbs reduces knee joint stress by 40 lbs per step.' },
            { title: 'Improves cartilage health', desc: 'Cartilage has no blood supply — it gets nutrients through the compression and release of movement. Exercise literally feeds your cartilage.' },
          ].map((item) => (
            <li key={item.title} className="flex items-start gap-3">
              <span className="text-[#c8a96e] mt-1 flex-shrink-0"><i className="ri-checkbox-circle-line" /></span>
              <div>
                <strong className="text-white">{item.title}:</strong>
                <span className="text-gray-400 text-sm ml-1">{item.desc}</span>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section id="best-exercises" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Best Exercises for Men with Arthritis Over 60</h2>
        <div className="space-y-4 mb-4">
          {[
            { category: 'Low-Impact Aerobic', exercises: ['Walking on flat surfaces', 'Stationary cycling', 'Swimming and water aerobics', 'Elliptical trainer'], note: 'These provide cardiovascular benefits and weight management without high joint impact.' },
            { category: 'Resistance Training', exercises: ['Seated leg press (limited range)', 'Dumbbell rows (supported)', 'Chest press (lying or seated)', 'Resistance band exercises', 'Bodyweight squats (partial range if needed)'], note: 'Build the muscle that protects arthritic joints. Use lighter weights with higher reps.' },
            { category: 'Flexibility & Mobility', exercises: ['Gentle stretching of affected joints', 'Yoga (modified for joint limitations)', 'Tai chi — proven to reduce arthritis pain', 'Range-of-motion exercises'], note: 'Maintain and improve joint mobility. Do these daily, especially on rest days.' },
          ].map((item) => (
            <div key={item.category} className="bg-[#111111] border border-white/10 rounded-lg p-5">
              <p className="text-[#c8a96e] text-sm font-black mb-3">{item.category}</p>
              <ul className="space-y-1 mb-3">
                {item.exercises.map((ex) => (
                  <li key={ex} className="flex items-start gap-2">
                    <span className="text-[#c8a96e] mt-1 flex-shrink-0 text-xs"><i className="ri-arrow-right-s-line" /></span>
                    <span className="text-gray-300 text-sm">{ex}</span>
                  </li>
                ))}
              </ul>
              <p className="text-gray-500 text-xs italic">{item.note}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="exercises-avoid" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Exercises to Modify or Avoid</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          These exercises may need modification or avoidance depending on which joints are affected:
        </p>
        <ul className="space-y-2 mb-4">
          {[
            'Deep squats below 90 degrees — excessive knee joint stress',
            'High-impact activities (running, jumping) during flare-ups',
            'Exercises that cause sharp, stabbing pain — distinguish from normal muscle soreness',
            'Heavy overhead pressing if you have shoulder arthritis',
            'Exercises that lock out joints under load',
          ].map((item) => (
            <li key={item} className="flex items-start gap-3">
              <span className="text-red-400 mt-1 flex-shrink-0"><i className="ri-close-circle-line" /></span>
              <span className="text-gray-300 text-sm">{item}</span>
            </li>
          ))}
        </ul>
        <p className="text-gray-300 leading-relaxed">
          The rule of thumb: mild discomfort during exercise is acceptable. Sharp pain, swelling, or pain that lasts more than 2 hours after exercise means you've done too much. Reduce intensity next time.
        </p>
      </section>

      <section id="joint-protection" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Joint Protection Principles</h2>
        <ul className="space-y-2 mb-4">
          {[
            'Warm up thoroughly — 10 minutes of light movement before any resistance training',
            'Use controlled, smooth movements — no jerking or bouncing',
            'Avoid locking out joints under load — keep a slight bend at the end of each rep',
            'Use lighter weights with higher reps (12-15) rather than heavy weights with low reps',
            'Rest adequately between sessions — arthritic joints need more recovery time',
            'Use heat before exercise to loosen stiff joints; ice after if there\'s inflammation',
          ].map((item) => (
            <li key={item} className="flex items-start gap-3">
              <span className="text-[#c8a96e] mt-1 flex-shrink-0"><i className="ri-shield-check-line" /></span>
              <span className="text-gray-300 text-sm">{item}</span>
            </li>
          ))}
        </ul>
      </section>

      <section id="flare-ups" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Exercising During Flare-Ups</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          During a significant arthritis flare-up, don't stop moving entirely — but do reduce intensity significantly:
        </p>
        <ul className="space-y-2 mb-4">
          {[
            'Switch to gentle range-of-motion exercises only',
            'Light walking is usually fine and helps reduce stiffness',
            'Avoid resistance training on severely inflamed joints',
            'Water exercise is ideal during flare-ups — buoyancy reduces joint load',
            'Once the flare subsides, return to your program gradually over 1-2 weeks',
          ].map((item) => (
            <li key={item} className="flex items-start gap-3">
              <span className="text-[#c8a96e] mt-1 flex-shrink-0"><i className="ri-arrow-right-s-line" /></span>
              <span className="text-gray-300 text-sm">{item}</span>
            </li>
          ))}
        </ul>
      </section>

      <section id="mason-approach" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Mason's Joint-Safe Approach</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          When I started training at 60, my knees hurt going up stairs. I couldn't do a traditional squat without pain. I had to figure out how to build strength without destroying my joints.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">
          The answer was joint-safe training — exercises and techniques specifically chosen to build strength while protecting arthritic and aging joints. That approach is the foundation of the <a href="https://masonhalefit.com" className="text-[#c8a96e] hover:text-[#d9bc82] underline font-semibold">Over-60 Strength Blueprint</a>.
        </p>
        <p className="text-gray-300 leading-relaxed">
          Within 6 months, my knee pain had significantly reduced. Within 18 months, I'd lost 55 lbs, gotten off 3 medications, and my joints felt better than they had in years. Stronger muscles protect joints. It really is that simple.
        </p>
      </section>

      <section id="faq" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-6">Frequently Asked Questions</h2>
        <div className="space-y-4">
          {[
            { q: 'Can men with arthritis lift weights?', a: 'Yes. Resistance training is actually recommended for arthritis management. Stronger muscles around arthritic joints provide better support and reduce pain. Use appropriate weights, avoid painful ranges of motion, and progress gradually.' },
            { q: 'Does exercise make arthritis worse?', a: 'No — the opposite is true. Regular, appropriate exercise reduces arthritis pain, improves joint function, and slows disease progression. Inactivity causes stiffness, muscle weakness, and increased pain.' },
            { q: 'What is the best exercise for arthritis in the knees?', a: 'Low-impact exercises that strengthen the muscles around the knee: cycling, swimming, leg press (limited range), straight leg raises, and seated leg extensions.' },
            { q: 'Should I exercise when my arthritis is flaring up?', a: 'During a significant flare-up, reduce intensity and focus on gentle range-of-motion exercises and light walking. Complete rest is rarely beneficial.' },
          ].map((item) => (
            <div key={item.q} className="bg-[#111111] border border-white/10 rounded-lg p-5">
              <p className="text-white font-bold mb-2">{item.q}</p>
              <p className="text-gray-400 text-sm leading-relaxed">{item.a}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="bg-[#c8a96e]/10 border border-[#c8a96e]/30 rounded-lg p-6 mb-10 text-center">
        <p className="text-[#c8a96e] font-black text-lg mb-2">Train Smart. Protect Your Joints.</p>
        <p className="text-gray-300 text-sm mb-4">The Over-60 Strength Blueprint is built around joint-safe movements specifically chosen for men over 60. Every exercise protects your joints while building the strength that reduces arthritis pain.</p>
        <a href="https://masonhalefit.com" className="inline-block bg-[#c8a96e] text-black font-black text-sm tracking-[0.15em] uppercase px-8 py-3 rounded-sm hover:bg-[#d9bc82] transition-all cursor-pointer whitespace-nowrap">
          Get The Blueprint — $19.99
        </a>
      </div>

      <AuthorBio />
      <RelatedArticles articles={relatedArticles} />
    </BlogLayout>
  );
}
